import { List, Map, OrderedMap } from 'immutable';
import { createSelector } from 'reselect';

import { convertNumberToE164 } from 'flow/utils/call-utils';
import {
    CallRecordKey,
    ExtensionKey,
    FavoriteKey,
    ProfileKey,
    QueueCallQueueKey,
    QueueKey,
} from 'flow/types/EntityKeys';
import { AutoReplyTemplateMessageType } from 'flow/types/AutoReplyTemplate';
import { QueueSortingData } from 'flow/types/Pbx';
import { State } from 'flow/types/State';
import { createTypedMap, TypedMap } from 'flow/types/TypedMap';
import {
    sortChannels,
    sortPbxServices as sortPbxServiceList,
    sortQueues,
} from 'flow/utils/pbx-sorting-utils';
import { selectOwnExtensionKey } from './ExtensionSelectors';
import {
    selectQueueAllSlice,
    selectQueueByPhoneNumberSlice,
} from 'flow/selectors/call-records-selectors';
import {
    selectCanAccessSupervisorView,
    selectOwnChatUserKey,
} from 'flow/selectors/own-extension-selectors';
import {
    sortPbxServices,
    sortQueuesSortingData,
} from 'flow/utils/pbx-utils-TW';
import { Favorite } from 'flow/types/Favorite';
import { OmniTicketQueueMapping } from 'flow/types/Queue';
import { normalizeOmnichannelChannelType } from 'flow/utils/omnichannel-utils';
import { isChatUserKey } from 'flow/utils';

// Raw `channelType` from the API is open-ended (`whatsapp`,
// `tickets_ms_email`, `tickets_google_email`, …). Normalize via the shared
// util, then map onto the AutoReply message-type enum.
// `normalizeOmnichannelChannelType` calls `.includes` without a null guard,
// so we have to bail out before reaching it if the field is missing.
const channelTypeToMessageType = (
    channelType: string | undefined,
): AutoReplyTemplateMessageType | null => {
    if (!channelType) return null;
    const normalized = normalizeOmnichannelChannelType(channelType);
    if (normalized === 'email') return 'EMAIL';
    if (normalized === 'whatsapp') return 'WHATSAPP';
    return null;
};

export const selectActiveCentionErrands = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['activeOmniErrands', 'byQueue', queueKey], Map());

export const selectActiveQueueItem = (
    state: State,
    queueKey: QueueKey,
    callRecordKey: CallRecordKey,
) =>
    state.top.getIn(['uniqueIdToChannel', callRecordKey]) ||
    state.pbx.getIn(
        ['activeOmniErrands', 'byQueue', queueKey, callRecordKey],
        Map(),
    );

export const selectQueues = (state: State) => state.pbx.get('queues');

export const selectAllQueueMembers = (state: State) =>
    state.pbx.get('queueMembers', Map());

const selectExtensionKeyParam = (_state: State, extensionKey: ExtensionKey) =>
    extensionKey;

// Had we used selector below, selectQueuesExtensionIsMemberOf, in components of which there can be multiple instances mounted
// simultaneously, receiving different extensionKeys, we would have created a factory function instead
// i.e. const makeSelectQueuesExtensionIsMemberOf = () => createSelector(...)
// That's something we can consider if we know we'll start using it in such a way.
// As it is now, it's only used in one place with one extensionKey at a time.
const selectQueuesExtensionIsMemberOf = createSelector(
    [selectQueues, selectAllQueueMembers, selectExtensionKeyParam],
    (queues, allQueueMembers, extensionKey) => {
        if (!extensionKey || !queues) {
            return Map();
        }
        return queues.filter((queue) => {
            const queueKey = queue.get('key');
            const members = allQueueMembers.get(queueKey);
            return members?.some(
                (member) => member.get('extensionKey') === extensionKey,
            );
        });
    },
);

export const selectAllQueuesForOwnExtension = createSelector(
    [selectQueues, selectAllQueueMembers, selectOwnExtensionKey],
    (queues, allQueueMembers, ownExtensionKey) => {
        if (!ownExtensionKey || !queues) {
            return Map();
        }
        return queues.filter((queue) => {
            const queueKey = queue.get('key');
            return allQueueMembers
                .get(queueKey)
                ?.some(
                    (member) => member.get('extensionKey') === ownExtensionKey,
                );
        });
    },
);

export const selectExtensionIsMemberOfSomeQueue = (
    state: State,
    extensionKey: ExtensionKey,
) => !selectQueuesExtensionIsMemberOf(state, extensionKey)?.isEmpty();

export const selectQueueActiveProfile = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'activeProfile']);

export const selectQueueActiveProfileAvailable = (
    state: State,
    queueKey: QueueKey,
) => state.pbx.getIn(['queues', queueKey, 'activeProfile', 'available']);

export const selectQueueActiveProfileDescription = (
    state: State,
    queueKey: QueueKey,
) => state.pbx.getIn(['queues', queueKey, 'activeProfile', 'description']);

// Queue call records live in the normalized store under state.callRecordsData.
// The scope-specific slice readers (selectQueueAllSlice, selectQueueByPhoneNumberSlice)
// and the date-group factories live in call-records-selectors alongside the rest
// of the call-records-data API; this file consumes them for queue-specific logic.

export const selectQueueCallRecordsWithBPartNextBefore = (
    state: State,
    queueKey: QueueKey,
    bPartNumber: string,
) =>
    selectQueueByPhoneNumberSlice(state, queueKey, bPartNumber).get(
        'nextBefore',
    );

export const olderQueueCallRecordsWithBPartAreAvailableInApi = (
    state: State,
    queueKey: QueueKey,
    bPartNumber: string,
) =>
    selectQueueCallRecordsWithBPartNextBefore(state, queueKey, bPartNumber) !==
    null;

export const selectQueueCallRecordsWithBPartNextSince = (
    state: State,
    queueKey: QueueKey,
    bPartNumber: string,
) =>
    selectQueueByPhoneNumberSlice(state, queueKey, bPartNumber).get(
        'nextSince',
    );

export const selectQueueCallRecordsNextBefore = (
    state: State,
    queueKey: QueueKey,
) => selectQueueAllSlice(state, queueKey).get('nextBefore');

export const hasMoreQueueCallRecords = (state: State, queueKey: QueueKey) =>
    selectQueueCallRecordsNextBefore(state, queueKey) !== null;

export const getQueueCallRecordsNextSince = (
    state: State,
    queueKey: QueueKey,
) => selectQueueAllSlice(state, queueKey).get('nextSince');

export const selectQueueDescription = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'description']);

const selectPhoneNumberParam = (
    _state: State,
    phoneNumber?: string | undefined,
) => phoneNumber;

// Factory: RedirectPresentationWidget can be mounted in multiple call items
// simultaneously (own + supervised co-listening), each with a different
// phoneNumber. A shared createSelector's last-call cache would thrash between
// instances, so each component instance needs its own memoized selector.
export const makeSelectQueueDescriptionFromNumber = () =>
    createSelector(
        [selectQueues, selectPhoneNumberParam],
        (queues, phoneNumber) => {
            if (!phoneNumber || !queues) {
                return null;
            }
            const normalizedInput = convertNumberToE164(phoneNumber);
            const match = queues.find(
                (queue) =>
                    queue?.getIn(['number', 'number']) === normalizedInput,
            );
            return match ? match.get('description') : null;
        },
    );

const selectQueueDTOSubtype = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'DTOSubtype']);

export const selectQueueIsOfTypeTicketsOmni = (
    state: State,
    queueKey: QueueKey,
) => selectQueueDTOSubtype(state, queueKey) === 'OmniTicketChannelQueueDTO';

export const selectQueueIsOfTypeCentionOmni = (
    state: State,
    queueKey: QueueKey,
) => selectQueueDTOSubtype(state, queueKey) === 'OmnichannelQueueDTO';

export const selectQueueHasOmniSupport = (state: State, queueKey: QueueKey) =>
    selectQueueIsOfTypeCentionOmni(state, queueKey) ||
    selectQueueIsOfTypeTicketsOmni(state, queueKey);

export const selectQueueEditable = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'editable']);

export const selectQueueKeywords = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'contact', 'keywords']);

export const selectQueuePhoneNumber = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'number', 'number']);

export const selectOmniTicketQueueMappings = (
    state: State,
    queueKey: QueueKey,
) => state.pbx.getIn(['queues', queueKey, 'omniTicketQueueMappings']);

// Derives the configured auto-reply message types from the channel mappings the
// queue fetch already stores at ['queues', queueKey, 'omniTicketQueueMappings'].
// Returns null until the queue has been fetched (callers treat this as "show
// all" so the auto-reply UI isn't hidden before the queue loads), or a Set of
// configured message types once it has (empty Set when the queue has no channel
// mappings).
// Shared createSelector with a (state, queueKey) input has a cache size of 1 —
// fine while PbxSettings and PbxAutoReply are the only consumers (separate
// routes, one queueKey at a time). If two simultaneously-mounted components
// ever need this, switch to a factory like makeSelectQueueDescriptionFromNumber
// above.
export const selectOmniTicketConfiguredMessageTypes = createSelector(
    [selectOmniTicketQueueMappings],
    (
        mappings: List<TypedMap<OmniTicketQueueMapping>> | undefined,
    ): ReadonlySet<AutoReplyTemplateMessageType> | null => {
        if (mappings == null) return null;
        const types = new Set<AutoReplyTemplateMessageType>();
        mappings.forEach((m) => {
            const messageType = channelTypeToMessageType(m.get('channelType'));
            if (messageType) types.add(messageType);
        });
        return types;
    },
);

export const selectQueueProfiles = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'profiles'], List());

export const selectQueueProfile = (
    state: State,
    queueKey: QueueKey,
    profileKey: ProfileKey,
) =>
    selectQueueProfiles(state, queueKey)?.find(
        (profile) => profile.get('key') === profileKey,
    );

export const selectQueueMembers = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queueMembers', queueKey]);

export const selectQueueMembersCount = (state: State, queueKey: QueueKey) =>
    selectQueueMembers(state, queueKey)?.size;

export const selectQueueStatsSummary = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['queues', queueKey, 'statistics'], Map());

export const makeSelectNumberAgentsLoggedInToQueue = () =>
    createSelector(
        [selectQueueMembers],
        (queueMembers) =>
            queueMembers?.filter((member) => member.get('loggedIn'))?.size,
    );

export const selectQueuesSortingData = (state: State) =>
    state.pbx.getIn(['sortingData', 'queues'], Map());

// A shared instance, so that a queue with no calls yields the same value every time
// and the selectors built on this stay memoized.
const NO_ACTIVE_QUEUE_CALLS = OrderedMap();
const NO_QUEUE_CALLS_BY_CHANNEL = Map();

const selectActiveQueueCalls = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(
        ['topActiveQueueCalls', 'byQueue', queueKey],
        NO_ACTIVE_QUEUE_CALLS,
    );

export const selectWaitingQueueItems = (state: State) =>
    state.pbx.getIn(['waitingQueueItems', 'byKey']);

export const selectWaitingQueueItem = (
    state: State,
    queueCallQueueKey: QueueCallQueueKey,
) => state.pbx.getIn(['waitingQueueItems', 'byKey', queueCallQueueKey]);

export const selectKeysWaitingQueueItems = (state: State, queueKey: QueueKey) =>
    state.pbx.getIn(['waitingQueueItems', 'byQueue', queueKey], List());

/**
 * The queue call waiting in a queue for a given caller.
 *
 * The caller's own channel is never reported to us — the events that carry a queue
 * name are the agent legs the call rings out to — so the caller's number is the
 * only thing tying a queue call to the channel that will end up handling it. Two
 * calls waiting from the same number in the same queue are indistinguishable here;
 * the one that joined first wins, and they resolve within seconds of each other.
 */
export const selectWaitingQueueCallForCaller = (
    state: State,
    queueKey: QueueKey,
    callerNumber: string,
) =>
    selectKeysWaitingQueueItems(state, queueKey)
        .map((queueCallKey: QueueCallQueueKey) =>
            selectWaitingQueueItem(state, queueCallKey),
        )
        .find(
            (waitingQueueCall) =>
                waitingQueueCall?.getIn(['caller', 'number']) === callerNumber,
        );

export const selectWaitingQueueItemJoinTime = (
    state: State,
    queueCallQueueKey: QueueCallQueueKey,
) =>
    state.pbx.getIn([
        'waitingQueueItems',
        'byKey',
        queueCallQueueKey,
        'joinTime',
    ]);

export const selectOngoingQueueCallAnsweredTime = (
    state: State,
    callRecordKey: CallRecordKey,
) => state.top.getIn(['uniqueIdToChannel', callRecordKey, 'answeredTime']);

const selectWaitingQueueItemKeysByQueue = (state: State) =>
    state.pbx.getIn(['waitingQueueItems', 'byQueue']);

// Position is not stored on the item; it is the 1-based index in the
// per-queue ordered list. Item keys carry their queue, so one flat index over
// all the ordered lists — rebuilt only when they change — lets every waiting
// ticket row look its position up in O(1) instead of scanning its queue.
const selectWaitingQueueItemPositions = createSelector(
    [selectWaitingQueueItemKeysByQueue],
    (itemKeysByQueue) =>
        Map<QueueCallQueueKey, number>().withMutations((index) => {
            itemKeysByQueue?.forEach((itemKeys: List<QueueCallQueueKey>) => {
                itemKeys.forEach((itemKey, position) =>
                    index.set(itemKey, position + 1),
                );
            });
        }),
);

export const selectWaitingQueueItemPosition = (
    state: State,
    queueCallQueueKey: QueueCallQueueKey,
) => selectWaitingQueueItemPositions(state).get(queueCallQueueKey);

export const makeSelectOngoingQueueCalls = () =>
    createSelector([selectActiveQueueCalls], (activeQueueCalls) =>
        activeQueueCalls.filter((value) => value === 'Up'),
    );

const selectQueueCallsByChannel = (state: State) =>
    state.pbx.get('queueCallsByChannel', NO_QUEUE_CALLS_BY_CHANNEL);

const selectQueueKeyArgument = (_state: State, queueKey: QueueKey) => queueKey;

/**
 * The queue's ongoing calls, each with what we know about the call record it will
 * appear as in the queue's history. Undefined against a channel we could never
 * match to a waiting call, so a channel carrying a queue name without belonging to
 * one of its calls stays distinguishable from a real call.
 *
 * Factory so each component instance gets its own memoized selector.
 */
export const makeSelectOngoingQueueCallRecords = () =>
    createSelector(
        [
            selectActiveQueueCalls,
            selectQueueCallsByChannel,
            selectQueueKeyArgument,
        ],
        (activeQueueCalls, queueCallsByChannel, queueKey) =>
            activeQueueCalls
                .filter((channelState: string) => channelState === 'Up')
                .map((_channelState: string, channelID: string) =>
                    queueCallsByChannel.getIn([channelID, queueKey]),
                ),
    );

export const makeSelectOwnUserIsMemberOfQueue = () =>
    createSelector(
        [selectQueueMembers, selectOwnExtensionKey],
        (queueMembers, ownExtensionKey) =>
            queueMembers?.some(
                (member) => member.get('extensionKey') === ownExtensionKey,
            ),
    );

export const makeSelectOwnUserIsLoggedInToQueue = () =>
    createSelector(
        [selectQueueMembers, selectOwnExtensionKey],
        (queueMembers, ownExtensionKey) =>
            queueMembers?.some(
                (member) =>
                    member.get('extensionKey') === ownExtensionKey &&
                    member.get('loggedIn'),
            ),
    );

// Selectors for selectDefaultPBXKey
const selectRefers = (state: State) => state.pbx.get('refers');
const selectChannels = (state: State) => state.channels.get('channels');
const selectVoicemails = (state: State) => state.pbx.get('voicemails');
const selectConnections = (state: State) => state.pbx.get('connections');

/**
 * True once a queue or an ivr would have shown up if the account had one.
 *
 * Both slices stay undefined until their own fetch answers, and reloadPbx
 * fires all five pbx requests at once, so anything ranked below them has to
 * wait - otherwise the landing target comes down to which request returned
 * first.
 */
const selectQueuesAndRefersHaveAnswered = createSelector(
    [selectQueues, selectRefers],
    (queues, refers) => queues !== undefined && refers !== undefined,
);

const selectDefaultQueueKey = createSelector(
    [selectQueues, selectAllQueueMembers, selectOwnExtensionKey],
    (queues, allQueueMembers, extensionKey) => {
        if (!queues || queues.isEmpty()) {
            return undefined;
        }
        return sortQueues(queues, allQueueMembers, extensionKey)
            ?.first()
            ?.get('key');
    },
);

const selectDefaultReferKey = createSelector([selectRefers], (refers) => {
    if (!refers || refers.isEmpty()) {
        return undefined;
    }
    return sortPbxServices(refers).first()?.get('key');
});

/**
 * The rest of the pbx navigation, below the queues and ivrs.
 * Without these an account that only has, for example, a shared voicemail gets
 * no key at all, and the pbx tab never resolves.
 */
const selectFallbackPBXKey = createSelector(
    [selectChannels, selectVoicemails, selectConnections, selectOwnChatUserKey],
    (channels, voicemails, connections, chatUserKey) => {
        // sortChannels puts the own channels first. It throws without a
        // chatUserKey to compare the members against.
        if (channels && !channels.isEmpty() && isChatUserKey(chatUserKey)) {
            return sortChannels(channels, chatUserKey).first()?.get('key');
        }
        if (voicemails && !voicemails.isEmpty()) {
            return sortPbxServices(voicemails).first()?.get('key');
        }
        // Connections come in as a List, not as a keyed map, so they need
        // the untyped sorter.
        if (connections && !connections.isEmpty()) {
            return sortPbxServiceList(connections).first()?.get('key');
        }

        return '';
    },
);

/** The pbx list's key for the supervisor overview row. */
export const SUPERVISOR_VIEW_KEY = 'supervisor-view';

/**
 * Memoized selector that returns the default PBX key to navigate to, in the
 * order the pbx navigation lists them.
 *
 * Each candidate is its own memoized selector. That keeps this combiner small,
 * and it means a change to one slice cannot make the others recompute.
 */
export const selectDefaultPBXKey = createSelector(
    [
        selectCanAccessSupervisorView,
        selectDefaultQueueKey,
        selectDefaultReferKey,
        selectFallbackPBXKey,
        selectQueuesAndRefersHaveAnswered,
    ],
    (
        hasSupervisorAccess,
        queueKey,
        referKey,
        fallbackKey,
        queuesAndRefersHaveAnswered,
    ) => {
        if (hasSupervisorAccess) {
            return SUPERVISOR_VIEW_KEY;
        }
        if (queueKey) {
            return queueKey;
        }
        if (referKey) {
            return referKey;
        }
        // A queue or an ivr outranks everything below. Answering before they
        // have would hand the landing target to whichever request won the
        // race, so hold out - the landing page waits in its empty pane and
        // redirects as soon as they answer.
        if (!queuesAndRefersHaveAnswered) {
            return '';
        }
        return fallbackKey;
    },
);

/**
 * True once any part of the pbx has answered. The landing page waits for this
 * before it gives up on a default key.
 *
 * Every slice stays undefined until its own fetch comes back, and a fetch that
 * fails dispatches no result at all. So this must cover the same sources as
 * selectDefaultPBXKey - an account whose queues and ivrs never
 * answer would otherwise wait forever, even with a voicemail in the store.
 */
export const selectPbxIsLoaded = createSelector(
    [
        selectQueues,
        selectRefers,
        selectChannels,
        selectVoicemails,
        selectConnections,
    ],
    (queues, refers, channels, voicemails, connections) =>
        !!(queues || refers || channels || voicemails || connections),
);

// Local favorites selectors to avoid circular dependency with favorite-selectors.ts
const selectFavorites = (state: State) => state.favorites;

const selectQueueFavorites = createSelector([selectFavorites], (favorites) => {
    if (!favorites) {
        return createTypedMap<Record<FavoriteKey, TypedMap<Favorite>>>();
    }
    return favorites.filter((favorite) => favorite.get('type') === 'queue');
});

export const selectFavoriteQueuesSortingData = createSelector(
    [selectQueueFavorites, selectQueuesSortingData],
    (favoriteQueues, queuesSortingData) =>
        favoriteQueues.reduce((acc, fav) => {
            const queueKey = fav.getIn(['favoriteData', 'key']) as QueueKey;
            const queue = queuesSortingData.get(queueKey);
            if (queue) {
                return acc.set(queueKey, queue);
            }
            return acc;
        }, createTypedMap<Record<QueueKey, TypedMap<QueueSortingData>>>()),
);

export const selectFavoriteQueuesSortingDataSorted = createSelector(
    [selectFavoriteQueuesSortingData],
    (favoriteQueuesSortingData) =>
        sortQueuesSortingData(favoriteQueuesSortingData),
);

export const selectQueuesSortingDataSortedByFavorite = createSelector(
    [selectQueuesSortingData, selectFavoriteQueuesSortingData],
    (queuesSortingData, favoriteQueues) => {
        const favorites = queuesSortingData.filter((queue) =>
            favoriteQueues.has(queue.get('key')),
        );
        const nonFavorites = queuesSortingData.filter(
            (queue) => !favoriteQueues.has(queue.get('key')),
        );

        return sortPbxServices(favorites)
            .concat(sortPbxServices(nonFavorites))
            .toList();
    },
);
